import { useState, useEffect } from "react";
import { apiFetch } from "../api";
import { fmtDate } from "../helpers";
import { MUSCLE_GROUPS, EXERCISE_CATALOGUE, CATALOGUE_CATEGORIES } from "../constants";
import { Icon } from "../icons";
import { HoldToDelete, AvatarCircle } from "../components/shared";

// ─── CATALOGUE MANAGER ────────────────────────────────────────────────────────
function CatalogueManager() {
  const defaultEntries = EXERCISE_CATALOGUE.map((e, i) => ({
    ...e, id: `cat_${i}`, isDefault: true, createdBy: null,
  }));

  const [catalogue, setCatalogue] = useState(defaultEntries);

  useEffect(() => {
    apiFetch("/admin/catalogue").then(rows => {
      const defaultNames = new Set(EXERCISE_CATALOGUE.map(e => e.name));
      const userCreated = rows
        .filter(r => !defaultNames.has(r.name))
        .map((r, i) => ({
          name: r.name, category: "user", muscles: r.muscle ? [r.muscle] : [],
          id: `usr_${i}`, isDefault: false, createdBy: `${r.use_count} user${r.use_count !== 1 ? "s" : ""}`,
        }));
      setCatalogue([...defaultEntries, ...userCreated]);
    }).catch(() => {});
  }, []);
  const [catFilter, setCatFilter]       = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [search, setSearch]             = useState("");
  const [editing, setEditing]           = useState(null);
  const [form, setForm]                 = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);

  const filtered = catalogue.filter(e => {
    const mc = catFilter === "all" || e.category === catFilter;
    const ms = sourceFilter === "all"
      || (sourceFilter === "default" && e.isDefault)
      || (sourceFilter === "custom"  && !e.isDefault);
    const mq = e.name.toLowerCase().includes(search.toLowerCase());
    return mc && ms && mq;
  });

  const defaultCount = catalogue.filter(e =>  e.isDefault).length;
  const customCount  = catalogue.filter(e => !e.isDefault).length;

  const startNew  = () => { setForm({ name:"", category:"bodybuilding", muscles:[], isDefault:false }); setEditing("new"); };
  const startEdit = (item) => { setForm({...item}); setEditing(item); };
  const handleDelete = (e) => { if (e.isDefault) return; setConfirmDelete(e); };
  const doDelete = async () => {
    try {
      await apiFetch(`/admin/catalogue/${encodeURIComponent(confirmDelete.name)}`, { method: "DELETE" });
      setCatalogue(p => p.filter(x => x.id !== confirmDelete.id));
    } catch (e) { console.error(e); }
    setConfirmDelete(null);
  };
  const toggleMuscle = (m) => setForm(p => ({ ...p, muscles: p.muscles.includes(m) ? p.muscles.filter(x=>x!==m) : [...p.muscles, m] }));
  const handleSave = () => {
    if (!form.name.trim()) return;
    if (editing === "new") {
      setCatalogue(p => [...p, { ...form, id:`cat_${Date.now()}`, isDefault:false, createdBy:"Admin" }]);
    } else {
      setCatalogue(p => p.map(e => e.id === editing.id ? { ...form } : e));
    }
    setEditing(null); setForm({});
  };

  const catBadgeColor = c => c==="powerlifting"?"var(--primary)":c==="weightlifting"?"var(--tertiary)":"var(--muted)";

  return (
    <div>
      {/* Stats row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:16 }}>
        {[
          { label:"TOTAL",        value: catalogue.length },
          { label:"DEFAULT",      value: defaultCount },
          { label:"USER-CREATED", value: customCount },
        ].map(s => (
          <div key={s.label} style={{ background:"var(--bg-low)", borderRadius:8, padding:"12px" }}>
            <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:22, fontWeight:700, color:"#b9c8de" }}>{s.value}</div>
            <div className="label-caps" style={{ fontSize:8, marginTop:4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
        <input placeholder="Search catalogue..." value={search}
          onChange={e=>setSearch(e.target.value)}
          style={{
            flex:1, minWidth:160, background:"var(--bg-low)", border:"1px solid rgba(68,71,76,0.35)",
            borderRadius:8, padding:"9px 12px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none",
          }}/>
        <button
          onClick={startNew}
          style={{
            padding:"8px 16px", borderRadius:8, border:"none",
            background:"#94a3b8", color:"#233143",
            fontFamily:"'Space Grotesk',sans-serif",
            fontSize:10, fontWeight:700, letterSpacing:"0.1em",
            cursor:"pointer", textTransform:"uppercase", flexShrink:0,
          }}
        >+ ADD</button>
      </div>

      {/* Category filter */}
      <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:8 }}>
        {["all",...CATALOGUE_CATEGORIES].map(c => (
          <button key={c} onClick={()=>setCatFilter(c)} style={{
            padding:"4px 10px", borderRadius:6, fontSize:9, fontWeight:700,
            fontFamily:"'Space Grotesk',sans-serif", letterSpacing:"0.08em", textTransform:"uppercase",
            cursor:"pointer", border:`1px solid ${catFilter===c?"rgba(185,200,222,0.3)":"rgba(68,71,76,0.3)"}`,
            background: catFilter===c ? "rgba(185,200,222,0.12)" : "var(--bg-low)",
            color: catFilter===c ? "#b9c8de" : "#8e9197",
          }}>{c==="all"?"ALL":c.toUpperCase()}</button>
        ))}
        {[["all","ALL"],["default","DEFAULT"],["custom","USER"]].map(([v,l]) => (
          <button key={v} onClick={()=>setSourceFilter(v)} style={{
            padding:"4px 10px", borderRadius:6, fontSize:9, fontWeight:700,
            fontFamily:"'Space Grotesk',sans-serif", letterSpacing:"0.08em", textTransform:"uppercase",
            cursor:"pointer", border:`1px solid ${sourceFilter===v?"rgba(185,200,222,0.3)":"rgba(68,71,76,0.3)"}`,
            background: sourceFilter===v ? "rgba(185,200,222,0.12)" : "var(--bg-highest)",
            color: sourceFilter===v ? "#b9c8de" : "#44474c",
          }}>{l}</button>
        ))}
      </div>

      <div style={{ fontSize:10, color:"#8e9197", marginBottom:10, fontFamily:"'Space Grotesk',sans-serif" }}>
        {filtered.length} result{filtered.length!==1?"s":""}
      </div>

      {/* Table */}
      <div style={{ background:"var(--bg-low)", borderRadius:8, overflow:"hidden" }}>
        {filtered.map((e, i) => (
          <div key={e.id} style={{
            display:"flex", alignItems:"center", gap:12, padding:"10px 14px",
            borderBottom: i < filtered.length-1 ? "1px solid rgba(68,71,76,0.12)" : "none",
          }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:12, fontWeight:700, color:"var(--text)", textTransform:"uppercase" }}>{e.name}</div>
              <div style={{ display:"flex", gap:4, marginTop:3, flexWrap:"wrap" }}>
                <span style={{ fontSize:9, fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, color:catBadgeColor(e.category), letterSpacing:"0.08em" }}>{e.category?.toUpperCase()}</span>
                {(e.muscles||[]).slice(0,2).map(m => (
                  <span key={m} style={{ fontSize:9, color:"#44474c", letterSpacing:"0.05em" }}>{m}</span>
                ))}
              </div>
            </div>
            <div style={{ display:"flex", gap:4 }}>
              <button className="btn btn-ghost btn-xs" onClick={()=>startEdit(e)}><Icon.Edit/></button>
              {e.isDefault ? (
                <span style={{
                  display:"inline-flex", alignItems:"center", justifyContent:"center",
                  width:28, height:26, borderRadius:4, border:"1px solid rgba(68,71,76,0.3)",
                  color:"#44474c", fontSize:11, cursor:"not-allowed",
                }} title="Default exercises cannot be deleted">🔒</span>
              ) : (
                <button className="btn btn-danger btn-xs" onClick={()=>handleDelete(e)}><Icon.Trash/></button>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign:"center", padding:"32px", color:"#8e9197", fontSize:12 }}>No exercises match your filters</div>
        )}
      </div>

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header">
              <div className="modal-title">Delete Exercise</div>
              <button className="modal-close" onClick={()=>setConfirmDelete(null)}><Icon.X/></button>
            </div>
            <div className="modal-body">
              <p style={{fontSize:13,lineHeight:1.6}}>
                Delete <strong>{confirmDelete.name}</strong> from the catalogue? This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={doDelete}><Icon.Trash/> Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit modal */}
      {editing !== null && (
        <div className="modal-overlay">
          <div className="modal modal-md">
            <div className="modal-header">
              <div className="modal-title">{editing==="new"?"Add to Catalogue":"Edit Catalogue Exercise"}</div>
              <button className="modal-close" onClick={()=>setEditing(null)}><Icon.X/></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <div className="form-label">Exercise Name</div>
                <input className="input" value={form.name||""} autoFocus placeholder="e.g. Barbell Squat"
                  onChange={e=>setForm(p=>({...p,name:e.target.value}))}/>
              </div>
              <div className="form-group">
                <div className="form-label">Category</div>
                <select className="select" value={form.category||"bodybuilding"}
                  onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
                  {CATALOGUE_CATEGORIES.map(c=>(
                    <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <div className="form-label">Muscles</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:4}}>
                  {MUSCLE_GROUPS.filter(m=>m!=="Full Body").map(m => {
                    const idx = (form.muscles||[]).indexOf(m);
                    const active = idx !== -1;
                    const isPrimary = idx === 0;
                    return (
                      <div key={m} className={`muscle-chip ${active?"active":""}`}
                        style={isPrimary ? {background:"#b9c8de",color:"#233143",borderColor:"#b9c8de"} : {}}
                        onClick={()=>toggleMuscle(m)}>
                        {m}{isPrimary && " ★"}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── USERS PANEL ──────────────────────────────────────────────────────────────
function UsersPanel({ onUsersLoaded, isSuperAdmin }) {
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [togglingAdmin, setTogglingAdmin] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [syncing, setSyncing]         = useState(false);
  const [syncResult, setSyncResult]   = useState(null);

  const toggleAdmin = async (u, newVal) => {
    setTogglingAdmin(u.id);
    try {
      await apiFetch(`/admin/users/${u.id}/admin`, { method: 'PATCH', body: { isAdmin: newVal } });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, is_admin: newVal } : x));
      if (userDetail?.user?.id === u.id) {
        setUserDetail(prev => ({ ...prev, user: { ...prev.user, is_admin: newVal } }));
      }
    } catch (e) { console.error(e); }
    setTogglingAdmin(null);
  };

  const doDeleteUser = async () => {
    const u = userDetail?.user || selectedUser;
    if (!u) return;
    setDeleting(true);
    try {
      await apiFetch(`/admin/users/${u.id}`, { method: "DELETE" });
      setUsers(prev => prev.filter(x => x.id !== u.id));
      setSelectedUser(null);
      setUserDetail(null);
      setConfirmDeleteUser(false);
    } catch (e) { console.error(e); }
    setDeleting(false);
  };

  useEffect(() => {
    apiFetch("/admin/users")
      .then(u => { setUsers(u); setLoading(false); if (onUsersLoaded) onUsersLoaded(u); })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUser = async (user) => {
    setSelectedUser(user);
    setDetailLoading(true);
    setSyncResult(null);
    try {
      const detail = await apiFetch(`/admin/users/${user.id}`);
      setUserDetail(detail);
    } catch (e) { console.error(e); }
    setDetailLoading(false);
  };

  const syncExercises = async (userId) => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await apiFetch(`/admin/users/${userId}/sync-exercises`, {
        method: "POST",
        body: EXERCISE_CATALOGUE.map(e => ({ name: e.name, muscles: e.muscles, category: e.category })),
      });
      setSyncResult(result);
      const detail = await apiFetch(`/admin/users/${userId}`);
      setUserDetail(detail);
    } catch (e) { console.error(e); }
    setSyncing(false);
  };

  if (loading) return (
    <div style={{ textAlign:"center", padding:"40px 0" }}>
      <div className="label-caps">LOADING USERS...</div>
    </div>
  );

  if (selectedUser) {
    const u = userDetail?.user || selectedUser;
    const exercises = userDetail?.exercises || [];
    const totalVol  = exercises.reduce((a,ex) => a + ex.sets.reduce((b,s) => b+s.reps*s.weight, 0), 0);
    const totalSets = exercises.reduce((a,ex) => a + ex.sets.length, 0);
    const topOrm    = exercises.filter(e=>e.oneRepMax).sort((a,b)=>b.oneRepMax-a.oneRepMax)[0];

    return (
      <div className="fade-in">
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <button className="btn btn-ghost btn-sm" onClick={()=>{setSelectedUser(null);setUserDetail(null);}}>
            ← BACK
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => !u.is_super_admin && setConfirmDeleteUser(true)}
              disabled={u.is_super_admin}
              title={u.is_super_admin ? "Cannot delete super admin" : undefined}
              style={{
                padding:"5px 12px", borderRadius:6,
                border:`1px solid ${u.is_super_admin ? "rgba(255,85,85,0.1)" : "rgba(255,85,85,0.3)"}`,
                background:`${u.is_super_admin ? "rgba(255,85,85,0.03)" : "rgba(255,85,85,0.08)"}`,
                color:`${u.is_super_admin ? "rgba(255,85,85,0.25)" : "rgba(255,85,85,0.7)"}`,
                fontFamily:"'Space Grotesk',sans-serif", fontSize:9, fontWeight:700,
                letterSpacing:"0.1em", textTransform:"uppercase",
                cursor: u.is_super_admin ? "not-allowed" : "pointer",
              }}
            >DELETE USER</button>
          )}
        </div>
        <div style={{
          background:"var(--bg-low)", borderRadius:8, padding:"16px",
          marginBottom:16, display:"flex", alignItems:"center", gap:14,
          borderLeft:"3px solid #b9c8de",
        }}>
          <AvatarCircle name={u.display_name || u.email} size={44} />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:14, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.03em" }}>{u.display_name || u.email}</div>
            <div className="label-caps" style={{ fontSize:8, marginTop:3 }}>{u.email}</div>
          </div>
          {u.is_admin && (
            <span style={{
              background:"rgba(185,200,222,0.15)", color:"#b9c8de",
              fontFamily:"'Space Grotesk',sans-serif", fontSize:9, fontWeight:700,
              letterSpacing:"0.1em", padding:"3px 8px", borderRadius:5,
            }}>ADMIN</span>
          )}
        </div>

        {detailLoading ? (
          <div style={{ textAlign:"center", padding:"32px 0" }}><div className="label-caps">LOADING...</div></div>
        ) : (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:16 }}>
              {[
                { label:"EXERCISES",  value: exercises.length },
                { label:"TOTAL SETS", value: totalSets },
                { label:"VOLUME",     value: `${(totalVol/1000).toFixed(1)}k` },
                { label:"TOP 1RM",    value: topOrm?`${topOrm.oneRepMax}`:"-" },
              ].map(s => (
                <div key={s.label} style={{ background:"var(--bg-low)", borderRadius:8, padding:"10px" }}>
                  <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:16, fontWeight:700, color:"#b9c8de" }}>{s.value}</div>
                  <div className="label-caps" style={{ fontSize:7, marginTop:3 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
              <div style={{ background:"var(--bg-low)", borderRadius:8, padding:"14px" }}>
                <div className="label-caps" style={{ fontSize:9, marginBottom:10 }}>ACCOUNT INFO</div>
                {[
                  ["Email",  u.email],
                  ["Name",   u.display_name||"—"],
                  ["Since",  u.created_at ? fmtDate(u.created_at.split("T")[0]) : "—"],
                ].map(([k,v]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:8 }}>
                    <span style={{ color:"#8e9197" }}>{k}</span>
                    <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, fontSize:10 }}>{v}</span>
                  </div>
                ))}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:11 }}>
                  <span style={{ color:"#8e9197" }}>Admin</span>
                  {isSuperAdmin ? (
                    <button onClick={() => toggleAdmin(u, !u.is_admin)} disabled={togglingAdmin === u.id}
                      style={{
                        padding:"3px 10px", borderRadius:5, fontSize:9, fontWeight:700,
                        fontFamily:"'Space Grotesk',sans-serif", cursor:"pointer", border:"none",
                        background: u.is_admin ? "rgba(255,180,171,0.15)" : "rgba(185,200,222,0.15)",
                        color: u.is_admin ? "var(--error)" : "#b9c8de",
                        opacity: togglingAdmin === u.id ? 0.5 : 1, letterSpacing:"0.08em",
                      }}>
                      {togglingAdmin === u.id ? "…" : u.is_admin ? "REVOKE" : "GRANT"}
                    </button>
                  ) : (
                    <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, fontSize:10 }}>{u.is_admin ? "YES" : "NO"}</span>
                  )}
                </div>
              </div>
              <div style={{ background:"var(--bg-low)", borderRadius:8, padding:"14px" }}>
                <div className="label-caps" style={{ fontSize:9, marginBottom:10 }}>TOP 1RMs</div>
                {exercises.filter(e=>e.oneRepMax).sort((a,b)=>b.oneRepMax-a.oneRepMax).slice(0,5).map((ex,i) => (
                  <div key={ex.id} style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:11 }}>
                    <span style={{ color:"#8e9197", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"50%" }}>{ex.name}</span>
                    <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, color:"#b9c8de", fontSize:11 }}>{ex.oneRepMax} lbs</span>
                  </div>
                ))}
                {exercises.filter(e=>e.oneRepMax).length === 0 && (
                  <div style={{ fontSize:11, color:"#44474c" }}>No 1RMs set</div>
                )}
              </div>
            </div>
            <div style={{ background:"var(--bg-low)", borderRadius:8, overflow:"hidden" }}>
              <div style={{ padding:"10px 14px", borderBottom:"1px solid rgba(68,71,76,0.15)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div className="label-caps" style={{ fontSize:9 }}>EXERCISES ({exercises.length})</div>
                <button
                  onClick={() => syncExercises(u.id)}
                  disabled={syncing}
                  style={{
                    padding:"4px 10px", borderRadius:5, fontSize:9, fontWeight:700,
                    fontFamily:"'Space Grotesk',sans-serif", letterSpacing:"0.08em", textTransform:"uppercase",
                    border:"1px solid rgba(185,200,222,0.2)", background:"rgba(185,200,222,0.07)",
                    color: syncing ? "#44474c" : "#94a3b8", cursor: syncing ? "not-allowed" : "pointer",
                  }}
                >{syncing ? "SYNCING..." : "SYNC CATALOGUE"}</button>
              </div>
              {syncResult && (
                <div style={{
                  padding:"8px 14px", fontSize:11,
                  color: syncResult.updated > 0 ? "#b9c8de" : "#8e9197",
                  borderBottom:"1px solid rgba(68,71,76,0.1)",
                  background: syncResult.updated > 0 ? "rgba(185,200,222,0.05)" : "transparent",
                }}>
                  {syncResult.updated > 0
                    ? `Updated ${syncResult.updated} exercise${syncResult.updated !== 1 ? "s" : ""}: ${syncResult.changes.map(c => c.old_name !== c.new_name ? `"${c.old_name}" → "${c.new_name}"` : `"${c.new_name}"`).join(", ")}`
                    : "Already up to date — no changes needed."}
                </div>
              )}
              {exercises.length === 0 ? (
                <div style={{ textAlign:"center", padding:"24px 0" }}><div className="label-caps" style={{ fontSize:9 }}>NO EXERCISES YET</div></div>
              ) : exercises.map((ex, i) => {
                const best = ex.sets.reduce((b,s) => s.weight>(b?.weight||0)||(s.weight===(b?.weight||0)&&s.reps>(b?.reps||0))?s:b, null);
                return (
                  <div key={ex.id} style={{
                    display:"flex", gap:12, padding:"9px 14px", alignItems:"center",
                    borderBottom: i < exercises.length-1 ? "1px solid rgba(68,71,76,0.1)" : "none",
                  }}>
                    <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:11, fontWeight:700, flex:1, textTransform:"uppercase", letterSpacing:"0.03em" }}>{ex.name}</div>
                    <div style={{ fontSize:10, color:"#8e9197", flexShrink:0 }}>{ex.sets.length} sets</div>
                    <div style={{ fontSize:10, color:"#8e9197", flexShrink:0 }}>{best ? `${best.weight}×${best.reps}` : "—"}</div>
                    <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:11, fontWeight:700, color:"#b9c8de", flexShrink:0 }}>
                      {ex.oneRepMax ? `${ex.oneRepMax} lbs` : "—"}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── DELETE USER CONFIRM MODAL ── */}
        {confirmDeleteUser && (() => {
          const u = userDetail?.user || selectedUser;
          return (
            <div className="modal-overlay">
              <div className="modal modal-sm">
                <div className="modal-header">
                  <div className="modal-title">Delete User</div>
                  <button className="modal-close" onClick={() => setConfirmDeleteUser(false)}><Icon.X /></button>
                </div>
                <div className="modal-body">
                  <p style={{ fontSize:13, lineHeight:1.6, marginBottom:16 }}>
                    Permanently delete <strong>{u?.display_name || u?.email}</strong> and all their data?
                    This includes all exercises, sets, and workouts. <strong>This cannot be undone.</strong>
                  </p>
                  <HoldToDelete onDelete={doDeleteUser} label={deleting ? "DELETING…" : "HOLD TO DELETE"} fullWidth />
                </div>
                <div className="modal-footer">
                  <button className="btn btn-ghost" onClick={() => setConfirmDeleteUser(false)}>Cancel</button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom:12, fontFamily:"'Space Grotesk',sans-serif", fontSize:10, color:"#8e9197", letterSpacing:"0.08em" }}>
        {users.length} REGISTERED USER{users.length!==1?"S":""}
      </div>
      <div style={{ background:"var(--bg-low)", borderRadius:8, overflow:"hidden" }}>
        {users.map((u, i) => (
          <div key={u.id} onClick={()=>loadUser(u)} style={{
            display:"flex", alignItems:"center", gap:12, padding:"12px 14px",
            borderBottom: i < users.length-1 ? "1px solid rgba(68,71,76,0.12)" : "none",
            cursor:"pointer", transition:"background 0.12s",
          }}>
            <AvatarCircle name={u.display_name || u.email} size={36} />
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:12, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.03em", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {u.display_name||u.email||"—"}
              </div>
              <div style={{ fontSize:10, color:"#8e9197", marginTop:2 }}>
                {u.set_count} sets · {u.total_volume ? `${(Number(u.total_volume)/1000).toFixed(1)}k lbs` : "0 lbs"}
              </div>
            </div>
            {isSuperAdmin ? (
              <button onClick={e => { e.stopPropagation(); toggleAdmin(u, !u.is_admin); }}
                disabled={togglingAdmin === u.id}
                style={{
                  padding:"4px 10px", borderRadius:5, fontSize:9, fontWeight:700,
                  fontFamily:"'Space Grotesk',sans-serif", cursor:"pointer", letterSpacing:"0.08em",
                  border:"1px solid",
                  background: u.is_admin ? "rgba(255,180,171,0.12)" : "rgba(185,200,222,0.1)",
                  color: u.is_admin ? "var(--error)" : "#b9c8de",
                  borderColor: u.is_admin ? "rgba(255,180,171,0.25)" : "rgba(185,200,222,0.2)",
                  opacity: togglingAdmin === u.id ? 0.5 : 1,
                }}>
                {togglingAdmin === u.id ? "…" : u.is_admin ? "ADMIN ✕" : "+ ADMIN"}
              </button>
            ) : (
              u.is_admin ? (
                <span style={{
                  background:"rgba(185,200,222,0.12)", color:"#b9c8de",
                  fontFamily:"'Space Grotesk',sans-serif", fontSize:9, fontWeight:700,
                  letterSpacing:"0.1em", padding:"3px 8px", borderRadius:5,
                }}>ADMIN</span>
              ) : null
            )}
          </div>
        ))}
        {users.length === 0 && (
          <div style={{ textAlign:"center", padding:"32px 0" }}><div className="label-caps">NO USERS YET</div></div>
        )}
      </div>
    </div>
  );
}

// ─── ADMIN VIEW ───────────────────────────────────────────────────────────────
export default function AdminView({ isSuperAdmin }) {
  const [tab, setTab]     = useState("users");
  const [users, setUsers] = useState([]);

  return (
    <div className="fade-in">

      {/* ── INFRASTRUCTURE HEALTH HERO ── */}
      <section style={{ marginBottom: 28 }}>
        <div style={{
          background: "var(--bg-low)", padding: "20px 24px",
          borderRadius: 8, position: "relative", overflow: "hidden",
          border: "1px solid rgba(68,71,76,0.12)",
        }}>
          <div style={{ position:"absolute", top:0, right:0, padding:12, opacity:0.07 }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="#b9c8de">
              <path d="M20 13C20 17.4 16.4 21 12 21s-8-3.6-8-8V5l8-3 8 3v8zM12 3.3L6 5.6v7.4c0 3.3 2.7 6 6 6s6-2.7 6-6V5.6L12 3.3z"/>
            </svg>
          </div>
          <div className="label-caps" style={{ fontSize:9, marginBottom:8 }}>INFRASTRUCTURE HEALTH</div>
          <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:10 }}>
            <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:36, fontWeight:900, color:"#b9c8de", letterSpacing:"-0.04em", fontStyle:"italic" }}>99.9%</div>
            <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:14, fontWeight:700, color:"#8e9197" }}>UPTIME</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ flex:1, height:3, background:"var(--bg-highest)", borderRadius:2, overflow:"hidden" }}>
              <div style={{ height:"100%", width:"99.9%", background:"#b9c8de", borderRadius:2 }}/>
            </div>
            <span className="label-caps" style={{ fontSize:8, color:"#b9c8de" }}>STABLE</span>
          </div>
        </div>
      </section>

      {/* ── TABS ── */}
      <nav style={{ display:"flex", gap:6, marginBottom:24, overflowX:"auto" }}>
        {[["users","USERS"],["catalogue","CATALOGUE"]].map(([id, label]) => (
          <button key={id} onClick={()=>setTab(id)} style={{
            padding:"8px 20px", borderRadius:8, cursor:"pointer",
            background: tab===id ? "#1e293b" : "var(--bg-low)",
            border: `1px solid ${tab===id ? "rgba(185,200,222,0.25)" : "rgba(68,71,76,0.3)"}`,
            color: tab===id ? "#f1f5f9" : "#64748b",
            fontFamily:"'Space Grotesk',sans-serif",
            fontSize:11, fontWeight:700, letterSpacing:"0.1em",
            textTransform:"uppercase", whiteSpace:"nowrap",
            transition:"all 0.15s",
          }}>{label}</button>
        ))}
      </nav>

      <div style={{ display: tab==="users" ? "block" : "none" }}>
        <UsersPanel onUsersLoaded={setUsers} isSuperAdmin={isSuperAdmin}/>
      </div>
      {tab==="catalogue" && <CatalogueManager />}
    </div>
  );
}
